import React from 'react'
import { Trash2, Plus } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { apiFetch } from '../../lib/api-client'

interface Knowledge {
  id: string
  category: string
  question: string
  answer: string
  troubleshooting: string[]
  escalate_when: string | null
  created_at: string | null
}

const CATEGORIES: { value: string; label: string }[] = [
  { value: 'vvs', label: 'VVS' },
  { value: 'varme', label: 'Varme' },
  { value: 'el', label: 'El' },
  { value: 'hvidevarer', label: 'Hvidevarer' },
  { value: 'leje_betaling', label: 'Leje / betaling' },
  { value: 'generel', label: 'Generel' },
]

function categoryLabel(v: string): string {
  return CATEGORIES.find((c) => c.value === v)?.label ?? v
}

export function LejerAISettingsPage() {
  const [rows, setRows] = React.useState<Knowledge[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)

  const [category, setCategory] = React.useState('vvs')
  const [question, setQuestion] = React.useState('')
  const [answer, setAnswer] = React.useState('')
  const [troubleshooting, setTroubleshooting] = React.useState('')
  const [escalateWhen, setEscalateWhen] = React.useState('')

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await apiFetch<Knowledge[]>('/api/v1/lejer-ai/knowledge'))
    } catch (e: unknown) {
      setError((e instanceof Error ? e.message : null) ?? 'Kunne ikke hente vidensbasen.')
    } finally {
      setLoading(false)
    }
  }, [])

  // One-shot mount-time data load.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  async function add(e: React.FormEvent) {
    e.preventDefault()
    if (!question.trim() || !answer.trim() || saving) return
    setSaving(true)
    setError(null)
    try {
      await apiFetch('/api/v1/lejer-ai/knowledge', {
        method: 'POST',
        body: JSON.stringify({
          category,
          question: question.trim(),
          answer: answer.trim(),
          troubleshooting: troubleshooting
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
          escalate_when: escalateWhen.trim() || null,
        }),
      })
      setQuestion('')
      setAnswer('')
      setTroubleshooting('')
      setEscalateWhen('')
      await load()
    } catch (e: unknown) {
      setError((e instanceof Error ? e.message : null) ?? 'Kunne ikke gemme.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(id: string) {
    setRows((rs) => rs.filter((r) => r.id !== id))
    try {
      await apiFetch(`/api/v1/lejer-ai/knowledge/${id}`, { method: 'DELETE' })
    } catch {
      void load()
    }
  }

  return (
    <AppLayout>
      <div style={{ padding: '0 0 24px 0' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 3 }}>Lejer-AI</div>
        <div style={{ fontSize: 14, color: '#6b7280' }}>
          Tilpas hvad assistenten svarer dine lejere. Dine svar lægges oven i den indbyggede
          danske vidensbase.
        </div>
      </div>

      {/* Add form */}
      <form
        onSubmit={add}
        style={{
          background: '#fff',
          borderRadius: 10,
          border: '1px solid #eef0f4',
          padding: 24,
          marginBottom: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Tilføj svar</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
            <span style={labelStyle}>Kategori</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 240 }}>
            <span style={labelStyle}>Spørgsmål / emne</span>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={300}
              placeholder="fx Toilettet løber"
              style={inputStyle}
            />
          </label>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>Svar</span>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder="Det svar lejeren skal have"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>Fejlfindingstrin (ét pr. linje)</span>
          <textarea
            value={troubleshooting}
            onChange={(e) => setTroubleshooting(e.target.value)}
            rows={3}
            placeholder={'Luk for stophanen\nTjek svømmeren i cisternen'}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={labelStyle}>Eskalér til udlejer når…</span>
          <input
            value={escalateWhen}
            onChange={(e) => setEscalateWhen(e.target.value)}
            maxLength={500}
            placeholder="fx Hvis det stadig løber efter ovenstående"
            style={inputStyle}
          />
        </label>
        {error && <div style={{ fontSize: 13, color: '#dc2626' }}>{error}</div>}
        <div>
          <button
            type="submit"
            disabled={saving || !question.trim() || !answer.trim()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: saving || !question.trim() || !answer.trim() ? '#c7d2fe' : '#4f46e5',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '9px 16px',
              fontSize: 14,
              fontWeight: 600,
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            <Plus size={15} /> Tilføj
          </button>
        </div>
      </form>

      {/* List */}
      <div
        style={{
          background: '#fff',
          borderRadius: 10,
          border: '1px solid #eef0f4',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>Henter…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 32, fontSize: 14, color: '#9ca3af' }}>
            Du har ingen egne svar endnu. Assistenten bruger den indbyggede vidensbase.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {rows.map((r) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  padding: '16px 20px',
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#4f46e5',
                        background: '#eef2ff',
                        padding: '2px 8px',
                        borderRadius: 20,
                      }}
                    >
                      {categoryLabel(r.category)}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{r.question}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{r.answer}</div>
                  {r.troubleshooting.length > 0 && (
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12, color: '#6b7280' }}>
                      {r.troubleshooting.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  )}
                  {r.escalate_when && (
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                      Eskalér når: {r.escalate_when}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => void remove(r.id)}
                  title="Slet"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: 6,
                    height: 'fit-content',
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
}

const inputStyle: React.CSSProperties = {
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 14,
  outline: 'none',
  fontFamily: 'inherit',
}
